// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — which body a dataset shows, when it is not the Earth.
 *
 * The owner's rule (2026-10-14, extending the Sun rule of 2026-10-02):
 * every body but the Earth gets the Sun's treatment. Io, the Moon, Mars or
 * any other body starts bare: no automatic overlays or borders, no ground
 * (the globe's own GIBS Earth) under it, no Earth map tiles, and no place
 * on Browse's Map view.
 *
 * The Sun keeps its own matching ({@link isSunStream}: the NASA SDO
 * organisation slug or R2 path, so a new SDO channel is covered). Any other
 * body is named by a `celestialBody` field (the SOS catalogue's, or a future
 * DSA's) or by a `body:<name>` keyword or tag, which is how the dataset
 * builder writes it today (DSA 1.7 and 1.8 allow no extra top-level field):
 * the builder's Io Volcanoes (e1d692b8fecaa12a) carries `body:io`.
 * `body:earth` and `celestialBody: "Earth"` (or "") stay the Earth. Never
 * matched by dataset id.
 *
 * The same rule and the same keyword are read by AYNI 2D (celestialBodyOf /
 * isOffEarthDataset, 2D 574832c) and AYNI 3D (isNonEarthDataset, 3D c8eefc78).
 */

import type { Dataset } from '../types'
import { isEarthBody } from './datasetOverlayOptions'

/** A keyword naming the body a dataset shows, as the builder writes it: `body:io`. */
const BODY_KEYWORD = /^body:\s*(.+)$/i

/**
 * A real-time Sun stream (NASA SDO: photosphere, 171 Å corona, 304 Å
 * chromosphere): matched on the publisher's org slug or R2 path, not the
 * row ids, so a new SDO channel is covered too.
 */
export function isSunStream(entry: { organizationSlug?: string; mpd?: string }): boolean {
  return entry.organizationSlug === 'nasa-sdo' || /(^|\/)nasa-sdo\//.test(entry.mpd ?? '')
}

/**
 * The body a `body:<name>` keyword or tag names, lower case, or null when
 * none does (or it names the Earth).
 */
export function bodyFromKeywords(words: readonly unknown[] | null | undefined): string | null {
  for (const word of words ?? []) {
    if (typeof word !== 'string') continue
    const named = BODY_KEYWORD.exec(word.trim())?.[1]?.trim().toLowerCase()
    if (named && named !== 'earth') return named
  }
  return null
}

/** What {@link offEarthBody} reads: an index row, a catalogue entry or a `.dsa`. */
export interface OffEarthInput {
  organizationSlug?: string
  mpd?: string
  celestialBody?: unknown
  keywords?: readonly unknown[]
  tags?: readonly unknown[]
}

/**
 * The body this row shows, lower case (`'sun'`, `'io'`, `'mars'`…), or null
 * for the Earth. The Sun first, by its own matching; then a `celestialBody`
 * field; then a `body:<name>` keyword or tag.
 */
export function offEarthBody(input: OffEarthInput | null | undefined): string | null {
  if (!input) return null
  if (isSunStream(input)) return 'sun'
  const field = typeof input.celestialBody === 'string' ? input.celestialBody : null
  if (field !== null && !isEarthBody(field)) return field.trim().toLowerCase()
  return bodyFromKeywords([...(input.keywords ?? []), ...(input.tags ?? [])])
}

/**
 * The body a loaded row shows, lower case, or null for the Earth, read the
 * way the row is once it is a `Dataset`: the Sun by its stream's path (its
 * rows carry no `celestialBody`, offEarthFields), any other body by the
 * `celestialBody` the index or its `.dsa` gave it (markOffEarth), or a
 * `body:<name>` tag. The Satellites layer asks this (owner, 2026-10-23):
 * satellites orbit the Earth and no other body, so a dataset of Saturn, the
 * Moon or the Sun draws none.
 */
export function bodyOfDataset(dataset: Pick<Dataset, 'dataLink' | 'celestialBody' | 'tags'> | null | undefined): string | null {
  if (!dataset) return null
  return offEarthBody({ mpd: dataset.dataLink, celestialBody: dataset.celestialBody, tags: dataset.tags })
}

/**
 * The fields a dataset from another body carries, so every surface treats
 * it as it treats the Sun: no automatic layers (mapLayers.defaultLayers),
 * no default borders, and, for a body other than the Sun, its
 * `celestialBody`, which keeps the Earth's tiles and ground from under it
 * on the browser globe (mapRenderer) and in the headset (photorealEarth).
 * The Sun's rows keep exactly the fields they had (no `celestialBody`).
 */
export function offEarthFields(body: string): { autoLayersOff: true; defaultBordersVisible: false; celestialBody?: string } {
  return body === 'sun'
    ? { autoLayersOff: true, defaultBordersVisible: false }
    : { autoLayersOff: true, defaultBordersVisible: false, celestialBody: body }
}

/**
 * Mark a loaded row as another body's, once its `.dsa` has named one
 * (main.ts resolveCelestialBody): the fields above, no place on Browse's
 * Map view (as the Sun has none), and no offer as a real-time layer over
 * the Earth.
 */
export function markOffEarth(dataset: Dataset, body: string): void {
  Object.assign(dataset, offEarthFields(body))
  delete dataset.boundingBox
  delete dataset.rtOverlayCandidate
}
